import React, { useState } from 'react';
import { Heart, MessageCircle, ChevronDown, ChevronUp, CheckCircle, AlertCircle } from 'lucide-react';
import colors from '../config/colors';

const InlineContributionForm = ({ schools, questionnaireData }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [preservedStrengths, setPreservedStrengths] = useState([]);
  const [missingStrengths, setMissingStrengths] = useState([]);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [insights, setInsights] = useState(null);
  const [error, setError] = useState('');

  // Q15/Q16 options mapping
  const strengthOptions = [
    { value: 'high_academic_standards', label: 'High academic standards' },
    { value: 'child_feels_safe_supported', label: 'Child feels safe and supported' },
    { value: 'teachers_know_child', label: 'Teachers know child as individual' },
    { value: 'diverse_international', label: 'Diverse international community' },
    { value: 'wide_range_activities', label: 'Wide range of activities' },
    { value: 'strong_university_placements', label: 'Strong university placements' },
    { value: 'fees_fair_value', label: 'Fees are fair value' },
    { value: 'curriculum_suits_well', label: 'Curriculum suits well' },
    { value: 'language_development', label: 'Good language development' }
  ];

  const handleSchoolSelect = (school) => {
    setSelectedSchool(school);
    setIsExpanded(true);
    setSubmitted(false);
    setInsights(null);
  };

  const toggleStrength = (value, isPreserved) => {
    if (isPreserved) {
      setPreservedStrengths(prev => 
        prev.includes(value) ? prev.filter(s => s !== value) : [...prev, value]
      );
    } else {
      setMissingStrengths(prev => 
        prev.includes(value) ? prev.filter(s => s !== value) : [...prev, value]
      );
    }
  };

  const handleSubmit = async () => {
    if (!selectedSchool) {
      setError('Please select a school first');
      return;
    }

    if (preservedStrengths.length === 0 && missingStrengths.length === 0) {
      setError('Please select at least one strength (preserved or missing)');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      const payload = {
        school_id: selectedSchool.id,
        school_name: selectedSchool.full_name,
        preserved_strengths: preservedStrengths,
        missing_strengths: missingStrengths,
        short_comment: comment || null,
        parent_priorities: questionnaireData?.q17_priorities || [],
        budget_range: questionnaireData?.q20_budget || questionnaireData?.q3_budget,
        location_preference: questionnaireData?.q21_location || questionnaireData?.q3_location
      };

      const response = await fetch(`${BACKEND_URL}/api/feedback/quick`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        setSubmitted(true);
        setInsights(result.insights);
        
        // Reset form
        setTimeout(() => {
          setPreservedStrengths([]);
          setMissingStrengths([]);
          setComment('');
          setIsExpanded(false);
        }, 5000);
      } else {
        setError('Failed to submit feedback. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
      console.error('Feedback submission error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div 
      style={{ 
        background: colors.background.secondary,
        borderRadius: '12px',
        padding: '24px',
        marginTop: '32px',
        border: `1px solid ${colors.border.light}`
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <Heart size={24} color={colors.accent.coral} />
        <h3 style={{ 
          fontSize: '20px', 
          fontWeight: '600', 
          color: colors.text.primary,
          margin: 0
        }}>
          Know one of these schools? Help other parents – rate it.
        </h3>
      </div>

      {/* School Selection Buttons */}
      {!isExpanded && (
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {schools.map(school => (
            <button
              key={school.id}
              onClick={() => handleSchoolSelect(school)}
              style={{
                padding: '10px 20px',
                background: colors.background.white,
                border: `2px solid ${colors.border.light}`,
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                color: colors.text.primary,
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = colors.accent.coral;
                e.target.style.background = '#FFF5F3';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = colors.border.light;
                e.target.style.background = colors.background.white;
              }}
            >
              <MessageCircle size={16} />
              Share feedback on {school.common_name || school.full_name}
            </button>
          ))}
        </div>
      )}

      {/* Expanded Form */}
      {isExpanded && !submitted && (
        <div style={{ marginTop: '20px' }}>
          {/* Selected School */}
          <div style={{ 
            marginBottom: '24px', 
            padding: '12px',
            background: colors.background.white,
            borderRadius: '8px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: '600', color: colors.text.primary }}>
                Sharing feedback for: {selectedSchool.full_name}
              </span>
              <button
                onClick={() => {
                  setIsExpanded(false);
                  setSelectedSchool(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.text.secondary,
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Change school
              </button>
            </div>
          </div>

          {/* Preserved Strengths */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '14px', 
              fontWeight: '600',
              color: colors.text.primary,
              marginBottom: '12px'
            }}>
              What's working well at this school? ✓
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {strengthOptions.map(option => (
                <button
                  key={option.value}
                  onClick={() => toggleStrength(option.value, true)}
                  style={{
                    padding: '8px 16px',
                    background: preservedStrengths.includes(option.value) 
                      ? colors.accent.green 
                      : colors.background.white,
                    color: preservedStrengths.includes(option.value) 
                      ? 'white' 
                      : colors.text.secondary,
                    border: `1px solid ${preservedStrengths.includes(option.value) 
                      ? colors.accent.green 
                      : colors.border.light}`,
                    borderRadius: '20px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    transition: 'all 0.2s'
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Missing Strengths */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '14px', 
              fontWeight: '600',
              color: colors.text.primary,
              marginBottom: '12px'
            }}>
              What could be better? ⚠️
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {strengthOptions.map(option => (
                <button
                  key={option.value}
                  onClick={() => toggleStrength(option.value, false)}
                  style={{
                    padding: '8px 16px',
                    background: missingStrengths.includes(option.value) 
                      ? '#FFA07A' 
                      : colors.background.white,
                    color: missingStrengths.includes(option.value) 
                      ? 'white' 
                      : colors.text.secondary,
                    border: `1px solid ${missingStrengths.includes(option.value) 
                      ? '#FFA07A' 
                      : colors.border.light}`,
                    borderRadius: '20px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    transition: 'all 0.2s'
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Optional Comment */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '14px', 
              fontWeight: '600',
              color: colors.text.primary,
              marginBottom: '8px'
            }}>
              Quick comment (optional)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Anything else to share?"
              maxLength={300}
              style={{
                width: '100%',
                minHeight: '80px',
                padding: '12px',
                borderRadius: '8px',
                border: `1px solid ${colors.border.light}`,
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
            <div style={{ 
              fontSize: '12px', 
              color: colors.text.secondary, 
              marginTop: '4px',
              textAlign: 'right'
            }}>
              {comment.length}/300
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div style={{ 
              padding: '12px', 
              background: '#FEE', 
              borderRadius: '8px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <AlertCircle size={18} color="#D32F2F" />
              <span style={{ color: '#D32F2F', fontSize: '14px' }}>{error}</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              width: '100%',
              padding: '14px',
              background: colors.accent.coral,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.6 : 1,
              transition: 'all 0.2s'
            }}
          >
            {submitting ? 'Submitting...' : 'Share Feedback'}
          </button>
        </div>
      )}

      {/* Success State with Insights */}
      {submitted && (
        <div style={{ marginTop: '20px' }}>
          <div style={{ 
            padding: '20px', 
            background: '#E8F5E9', 
            borderRadius: '12px',
            marginBottom: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <CheckCircle size={24} color={colors.accent.green} />
              <span style={{ fontWeight: '600', color: colors.accent.green, fontSize: '16px' }}>
                Thank you! Your feedback helps other parents.
              </span>
            </div>
          </div>

          {/* Bonus Insights */}
          {insights && insights.similar_schools && insights.similar_schools.length > 0 && (
            <div style={{ 
              padding: '16px', 
              background: colors.background.white,
              borderRadius: '8px',
              border: `1px solid ${colors.border.light}`
            }}>
              <div style={{ fontWeight: '600', color: colors.text.primary, marginBottom: '12px' }}>
                💡 Insight: Parents with similar preferences also shortlisted:
              </div>
              <ul style={{ 
                listStyle: 'none', 
                padding: 0, 
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                {insights.similar_schools.map((school, idx) => (
                  <li key={idx} style={{ 
                    fontSize: '14px',
                    color: colors.text.secondary,
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}>
                    <span>{school.school_name}</span>
                    <span style={{ 
                      fontSize: '12px',
                      color: colors.text.tertiary,
                      fontStyle: 'italic'
                    }}>
                      ({school.match_count} parents)
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InlineContributionForm;
